import {
  Bank,
  BonusAcceptanceHandling,
  BranchPasswordPolicySettings,
  EmailVerificationStatus,
  PersonGender,
  PixKeyType,
  PrivacyPolicyStatus,
  RegisterRequirements,
  SecurityQuestion,
  TermsAndConditionsStatus,
  UserCreationResultErrors,
  UserIdentificationType,
  UserLogonResultState,
} from '@arland-bmnext/api-data'
import useTranslation from 'next-translate/useTranslation'
import Router from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useCommonContext } from '../../../../context/common.context'
import usePromotionCode from '../../../../hooks/usePromotionCode'
import useSportRadarTagManager from '../../../../hooks/useSportRadarTagManager'
import { buildFormFieldFromRequiredField, getBonusAcceptanceField, getPromotionCodeField } from '../../../../lib/kyc'
import {
  RegisterResult,
  registerUser,
  signInUser,
  useUser,
  useUserAccounts,
  useUserBetSettings,
} from '../../../../lib/user'
import { EnumFlagTranslator } from '../../../../util/enum-translator'
import { gtmPushRegistrationFinishedEvent, gtmPushRegistrationStartedEvent } from '../../../../util/google-tag-manager'
import { isNullOrEmpty } from '../../../../util/string'
import { overrideWithTranslatedName } from '../../../../util/translate'
import { FormFieldConfig, FormFieldType, setFormData } from '../../../forms/FormBuilder'
import { RegistrationRequest, RequiredUserFields } from '../../../../lib/api-client/account'
import useIncomeAccessBtag from '../../../../hooks/useIncomeAccessBtag.ts'
import { useAccountSettings } from '../../../../lib/content'
import { DefaultRegistrationForm } from './DefaultRegistrationForm'
import { CpfRegistrationForm } from './CpfRegistrationForm'
import dayjs from 'dayjs'
import {
  ExatoFaceRecognitionFormProps,
  useExatoFaceRecognitionResultHandler,
} from '../../../../hooks/useExatoFaceRecognition'
import { Translate } from 'next-translate'
import { BooleanValidator } from '../../../../util/validators'

export type CheckPoint = {
  enabled: boolean
  label: string
}

export type VersionableCheckPoint = CheckPoint & {
  title: string
  body: string
  version: number | null
}

type RegistrationFormProps = {
  title: string
  description: string
  requirements: RegisterRequirements
  passwordPolicy: BranchPasswordPolicySettings
  securityQuestions: SecurityQuestion[]
  banks: Bank[]
  onRegistrationEmailVerificationNeeded: (email: string) => void
  redirectTo?: string
  showBonusAcceptanceSelector?: boolean
  showPromotionCodeField?: boolean
  optInMarketing?: CheckPoint
  termsAndConditionsCheckPoint?: VersionableCheckPoint
  financeMinistryCheckPoint?: CheckPoint
  privacyPolicyCheckPoint: VersionableCheckPoint
  exatoFaceRecognitionFormProps: ExatoFaceRecognitionFormProps
}
export const RegistrationForm = ({
  title,
  description,
  requirements,
  passwordPolicy,
  securityQuestions,
  banks,
  onRegistrationEmailVerificationNeeded,
  redirectTo,
  showBonusAcceptanceSelector,
  showPromotionCodeField,
  optInMarketing,
  termsAndConditionsCheckPoint,
  privacyPolicyCheckPoint,
  financeMinistryCheckPoint,
  exatoFaceRecognitionFormProps,
}: RegistrationFormProps) => {
  const { srtmPushRegistrationStarted, srtmPushRegistrationFinished } = useSportRadarTagManager()
  const { t, lang } = useTranslation('register')
  const { user, mutateUser } = useUser()
  const { mutateUserAccounts } = useUserAccounts()
  const { mutateUserBetSettings } = useUserBetSettings()
  const accountSettings = useAccountSettings()
  const cpfCheckActive = accountSettings?.cpfCheck?.enabled && accountSettings?.cpfCheck?.autoFillPersonalData
  const { countries, languages, timeZones, nodeCountries, getLanguageByShortSign } = useCommonContext()
  const language = getLanguageByShortSign(lang)
  const [errorMessage, setErrorMessage] = useState(null)

  const [defaultValues, setDefaultValues] = useState({})
  const [formFields, setFormFields] = useState([])
  const formDataRef = useRef<any>(null)

  const { promotionCode, hidePromotionCode } = usePromotionCode()
  const { btag } = useIncomeAccessBtag()

  const sentRegistrationStartedEvent = useRef(false)

  useEffect(() => {
    if (user != null && countries != null && accountSettings != null) {
      setInitialFormState()
      initializeForm()
    }
  }, [user, countries, lang, promotionCode, hidePromotionCode, accountSettings])

  useExatoFaceRecognitionResultHandler(
    setErrorMessage,
    async () => await onSubmit(formDataRef.current, exatoFaceRecognitionFormProps.identityValidationId),
    exatoFaceRecognitionFormProps.identityValidationId,
    exatoFaceRecognitionFormProps.statusMessage,
  )

  const notifyFormIsDirty = () => {
    if (sentRegistrationStartedEvent.current !== true) {
      srtmPushRegistrationStarted()
      gtmPushRegistrationStartedEvent()
      sentRegistrationStartedEvent.current = true
    }
  }

  const notifyPixKeyTypeChanged = (pixKeyType: PixKeyType) => {
    setFormFields(buildFormFields(pixKeyType))
  }

  const setInitialFormState = () => {
    const dv = {
      nationalityCountryId: user?.branch?.defaultCountryId,
      countryId: user?.branch?.defaultCountryId,
      gender: PersonGender.Male,
      languageId: user?.branch['defaultLanguageId'],
      timeZoneId: user?.timeZoneId,
      phoneNumber: countries?.find((c) => c.id === user?.branch?.defaultCountryId)?.callingCode?.toString(),
      promotionCode: promotionCode,
      bonusAcceptance: BonusAcceptanceHandling.AlwaysAsk,
      birthday: dayjs().add(-18, 'years').toDate(),
    }
    setDefaultValues(dv)
  }

  const initializeForm = () => {
    setFormFields(buildFormFields())
  }

  const buildFormFields = (pixKeyType?: PixKeyType) => {
    const fields: FormFieldConfig[] = []

    const _countries = overrideWithTranslatedName(countries, language.id)
    const _nodeCountries = []
    if (!isNullOrEmpty(nodeCountries)) {
      nodeCountries?.forEach((x) => {
        const translatedNodeCountry = _countries.find((y) => y.id === x.id)
        _nodeCountries.push(translatedNodeCountry)
      })
    }

    const zipCodeField = requirements.requiredFields.find((x) => x.objectProperty === RequiredUserFields.PRIMARYZIPCODE)

    for (let rf of requirements.requiredFields) {
      // Using separate cpf form
      if (cpfCheckActive && rf.objectProperty === RequiredUserFields.NATIONALIDENTITYID) continue

      // For cpf registration the country field is hidden, so it is set per default to brazil
      if (cpfCheckActive && rf.objectProperty === RequiredUserFields.PRIMARYCOUNTRY) continue

      // Skip pix key if type is not specified
      if (
        cpfCheckActive &&
        rf.objectProperty === RequiredUserFields.PIXKEY &&
        (isNullOrEmpty(pixKeyType) || pixKeyType === PixKeyType.None)
      )
        continue

      const field = buildFormFieldFromRequiredField(t, true, rf, {
        countries: _countries,
        nodeCountries: _nodeCountries,
        passwordPolicy,
        languages,
        timeZones,
        securityQuestions: overrideWithTranslatedName(securityQuestions, language.id),
        pixKeyType,
        banks,
        autoFillAddressFromZipCode: accountSettings.autoFillAddressFromZipCode && zipCodeField != null,
      })

      fields.push(field)
    }

    if (!hidePromotionCode && showPromotionCodeField) fields.push(getPromotionCodeField(t))

    if (showBonusAcceptanceSelector) {
      fields.push(getBonusAcceptanceField(t))
    }

    if (optInMarketing?.enabled) {
      fields.push(
        getCheckpointField(
          t,
          'sendNewsLetterEmail',
          optInMarketing,
          false,
          true,
          'account:Account.sendNewsLetterEmail',
        ),
      )
    }

    if (termsAndConditionsCheckPoint?.enabled) {
      fields.push(
        getVersionableCheckpointField(
          t,
          'acceptTnc',
          termsAndConditionsCheckPoint,
          false,
          false,
          'account:Account.termsAndConditions',
          BooleanValidator(true, t('account:Account.termsAndConditionsValidationMessage')),
        ),
      )
    }

    if (privacyPolicyCheckPoint?.enabled) {
      fields.push(
        getVersionableCheckpointField(
          t,
          'acceptPrivacyPolicy',
          privacyPolicyCheckPoint,
          false,
          false,
          'account:Account.acceptPrivacyPolicy',
          BooleanValidator(true, t('account:Account.acceptPrivacyPolicyValidationMessage')),
        ),
      )
    }

    if (financeMinistryCheckPoint?.enabled) {
      fields.push(
        getCheckpointField(
          t,
          'acceptDataSendToGov',
          financeMinistryCheckPoint,
          false,
          false,
          'account:Account.acceptFinanceMinistry',
          BooleanValidator(true, t('account:Account.acceptFinanceMinistryValidationMessage')),
        ),
      )
    }

    return fields
  }

  const buildRegistrationRequest = (formData: Partial<RegistrationRequest>) => {
    const request: RegistrationRequest = {}

    setFormData(formData, formFields, request)

    request.currencyId = user?.branch?.defaultCurrencyId

    if (request.languageId == null && user?.branch != null) request.languageId = user.branch['defaultLanguageId']

    if (request.bonusAcceptance == null) request.bonusAcceptance = BonusAcceptanceHandling.AlwaysAsk

    if (request.promotionCode == null && promotionCode != null && hidePromotionCode) {
      request.promotionCode = promotionCode
    }

    if (btag != null) {
      request.externalIdentifier = btag
    }

    // For cpf registration the country field is hidden, so it is set per default to brazil
    if (cpfCheckActive) request.countryId = countries.find((c) => c.shortSign === 'BR')?.id

    // Get the pix key from the formData or set it to the users cpf
    if (cpfCheckActive && request.pixKeyType) {
      switch (request.pixKeyType) {
        case PixKeyType.Cpf:
          request.pixKey = request.nationalIdentityId
          break
        case PixKeyType.Email:
          request.pixKey = request['pixKey_email']
          break
        case PixKeyType.Telefone:
          request.pixKey = request['pixKey_phone']
          break
      }

      if (request['pixKey_email']) delete request['pixKey_email']
      if (request['pixKey_phone']) delete request['pixKey_phone']
      if (request['pixKey_cpf']) delete request['pixKey_cpf']
    }

    // Set current version of TnC & Privacy
    if (termsAndConditionsCheckPoint) {
      request.termsAndConditionsVersion = termsAndConditionsCheckPoint.version
      request.termsAndConditionsStatus = TermsAndConditionsStatus.Accepted
    }

    if (privacyPolicyCheckPoint) {
      request.privacyPolicyVersion = privacyPolicyCheckPoint.version
      request.privacyPolicyStatus = PrivacyPolicyStatus.Accepted
    }

    return request
  }

  const handleSuccessfulRegistration = async (
    request: RegistrationRequest,
    result: RegisterResult,
    formData: Partial<RegistrationRequest>,
  ) => {
    // Push Sportradar Tag Manager Event
    srtmPushRegistrationFinished(result?.user?.id, formData?.promotionCode)

    // Push Google Tag Manager Event
    gtmPushRegistrationFinishedEvent(result?.user?.id, formData?.promotionCode)

    // Check for email verification status
    if (result.user.emailVerificationStatus === EmailVerificationStatus.Verified) {
      const identificationType = !isNullOrEmpty(request.logonName)
        ? UserIdentificationType.Username
        : UserIdentificationType.EmailAddress
      const identifier = !isNullOrEmpty(request.logonName) ? request.logonName : request.emailAddresse

      const loginResult = await signInUser(identificationType, identifier, request.password)

      // Check if login succeeded
      if (loginResult.state === UserLogonResultState.Ok) {
        await Promise.all([mutateUser(), mutateUserAccounts(), mutateUserBetSettings()])
        const redirectToQuery = redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''
        await Router.push('/onboarding' + redirectToQuery)
      }
    } else {
      onRegistrationEmailVerificationNeeded(request.emailAddresse)
    }
  }

  const onSubmit = async (formData: Partial<RegistrationRequest>, faceRecognitionIdentityValidationId?: string) => {
    formDataRef.current = formData

    setErrorMessage(null)

    const request = buildRegistrationRequest(formData)

    const result = await registerUser(request, faceRecognitionIdentityValidationId)

    // Check for cpf validation
    if (result?.isCpfValid === false) {
      return setErrorMessage(t('RegisterPage.cpfInvalid'))
    }

    switch (result.errors) {
      case UserCreationResultErrors.None:
        await handleSuccessfulRegistration(request, result, formData)
        break
      case UserCreationResultErrors.FaceRecognitionInvalid:
        exatoFaceRecognitionFormProps.onFaceRecognitionRequired(result.faceRecognitionUrl)
        break
      default:
        setErrorMessage(EnumFlagTranslator(t, UserCreationResultErrors, result.errors))
    }
  }

  return (
    <>
      {cpfCheckActive ? (
        <CpfRegistrationForm
          title={title}
          description={description}
          formFields={formFields}
          defaultValues={defaultValues}
          onRegistrationFormCompleted={onSubmit}
          errorMessage={errorMessage}
          notifyFormIsDirty={notifyFormIsDirty}
          notifyPixKeyTypeChanged={notifyPixKeyTypeChanged}
        />
      ) : (
        <DefaultRegistrationForm
          title={title}
          description={description}
          formFields={formFields}
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          errorMessage={errorMessage}
          notifyFormIsDirty={notifyFormIsDirty}
        />
      )}
    </>
  )
}

export const getCheckpointField = (
  t: Translate,
  fieldName: string,
  checkPoint: CheckPoint,
  required: boolean,
  showOptionalIfNotRequired: boolean,
  defaultLabelMsgId: string,
  validators?: any,
): FormFieldConfig => {
  return {
    name: convertParagraphs(checkPoint.label) ?? t(defaultLabelMsgId),
    helpText: undefined,
    property: fieldName,
    required,
    showOptionalIfNotRequired,
    formFieldType: FormFieldType.RadioGroupElement,
    selectOptions: [
      { name: t('common:Common.yes'), value: true },
      { name: t('common:Common.no'), value: false },
    ],
    validators,
  }
}

export const getVersionableCheckpointField = (
  t: Translate,
  fieldName: string,
  checkPoint: VersionableCheckPoint,
  required: boolean,
  showOptionalIfNotRequired: boolean,
  defaultLabelMsgId: string,
  validators?: any,
  helpTextTitleMsgId?: string,
): FormFieldConfig => {
  return {
    name: convertParagraphs(checkPoint.label) ?? t(defaultLabelMsgId),
    helpText: checkPoint.body
      ? {
          title: checkPoint.title ?? (helpTextTitleMsgId ? t(helpTextTitleMsgId) : undefined),
          description: <div dangerouslySetInnerHTML={{ __html: checkPoint.body }} />,
        }
      : undefined,
    property: fieldName,
    showOptionalIfNotRequired,
    required,
    formFieldType: FormFieldType.RadioGroupElement,
    selectOptions: [
      { name: t('common:Common.yes'), value: true },
      { name: t('common:Common.no'), value: false },
    ],
    validators,
  }
}

export const convertParagraphs = (val: string) => {
  if (!val) return val

  const root = document.createElement('div')
  const frag = document.createDocumentFragment()

  root.innerHTML = val

  let node = root.firstChild

  const subNodeBuffer = []
  const nodeBuffer = []

  while (node !== null) {
    if ('tagName' in node && node.tagName == 'P') {
      let subNode = node.firstChild
      subNodeBuffer.length = 0
      while (subNode !== null) {
        subNodeBuffer.push(subNode)
        subNode = subNode.nextSibling
      }
      for (subNode of subNodeBuffer) {
        nodeBuffer.push(subNode)
      }

      if (node.nextSibling) {
        nodeBuffer.push(document.createElement('BR'))
      }
    } else {
      nodeBuffer.push(node)
    }

    node = node.nextSibling
  }

  let brCount = 0
  for (node of nodeBuffer) {
    if ('tagName' in node && node.tagName == 'BR') {
      brCount++
      if (brCount > 2) {
        brCount = 0
        continue
      }
    }
    frag.append(node)
  }

  const target = document.createElement('div')
  target.append(frag)

  return target.innerHTML
}
